import React, {
  Activity,
  startTransition,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _get from 'lodash/get';
import { useLocalStorage } from 'usehooks-ts';
import { SystemAdmin, User } from 'graphql-common';
import Menu, { NavItem, NavItemTypeEnum } from '@lib/components/Menu/Menu';
import ErrorBoundary from '@lib/components/ErrorBoundary/ErrorBoundary';
import BreadCrumbsDisplay from '@lib/components/BreadCrumbs/BreadCrumbsDisplay';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import AccountPreview from '@lib/components/AccountPreview/AccountPreview';
import { downloadLinkId, emptyHiddenInputId } from '@lib/enums/selectors';
import ScrollToTop from '@lib/components/ScrollToTop/ScrollToTop';
import isMobile from '@lib/utils/isMobile';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import useWindowDimensions from '@lib/hooks/useWindowDimensions';
import ReleaseTracker from '@lib/components/ReleaseTracker/ReleaseTracker';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Logo from '@lib/components/Logo/Logo';
import LogoImage from '@lib/assets/images/logo-image.svg?react';
import { DASHBOARD_LAYOUT_IS_COLLAPSED } from '@lib/enums/localStorageKeys';
import UpgradePlanModal from '@lib/components/UpgradePlanModal/UpgradePlanModal';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { PlanState } from '@lib/enums/plan';
import TechnicalPauseTracker from '@lib/components/TechnicalPauseTracker/TechnicalPauseTracker';
import TechnicalPauseBanner from '@lib/components/TechnicalPauseBanner/TechnicalPauseBanner';
import { TechnicalPause } from '@lib/hooks/useTechnicalPause';
import Icons from '@lib/components/Icons/Icons';
import styles from './DashboardLayout.module.scss';

const {
  VITE_PLATFORM_URL: domain,
  VITE_RELEASE_VERSION: releaseVersion,
  VITE_RELEASE_TRACKER_SECONDS: releaseTrackerSeconds,
  VITE_BLOCK_UI_BY_DEVICE,
  MODE: mode,
} = import.meta.env;
const shouldBlockUiByDevice = VITE_BLOCK_UI_BY_DEVICE === 'true';

interface Props {
  activeTechnicalPause: TechnicalPause | null | undefined;
  captureException: (error: Error, response?: unknown) => void;
  disableContentScroll?: boolean;
  loading?: boolean;
  logoUrl: string;
  navItems: NavItem<undefined>[];
  outletContext: object;
  profileMenuItems: NavItem<unknown>[];
  settingsMenuNavItems?: NavItem<undefined>[];
  settingsPaths?: string[];
  showPlatformSwitcher?: boolean;
  showSidebarWhenTechnicalPause?: boolean;
  sideBarClassName?: string;
  technicalPauseWhiteUrlList?: string[];
  tools?: React.ReactNode;
  topComponents?: React.ReactNode;
  upcomingTechnicalPause: TechnicalPause | null | undefined;
  user: User | SystemAdmin;
}

// Header component (memoized) moved outside to reduce parent re-renders
interface HeaderProps {
  pathname: string;
  tools?: React.ReactNode;
  user: User | SystemAdmin;
  profileMenuItems: NavItem<unknown>[];
}

const DashboardHeader = React.memo(function DashboardHeader(
  props: HeaderProps,
) {
  const { pathname, tools, user, profileMenuItems } = props;
  const shimmerInnerRef = useRef<HTMLSpanElement | null>(null);

  // Effect Event: stable function used inside effect without changing deps
  const restartShimmer = useEffectEvent(() => {
    const el = shimmerInnerRef.current;
    if (!el) return;
    el.classList.remove(styles.shimmerActive);
    // Force reflow to restart the animation
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetWidth;
    el.classList.add(styles.shimmerActive);
  });

  // Re-trigger shimmer animation on pathname change without causing re-deps churn
  useEffect(() => {
    restartShimmer();
  }, [pathname]);

  return (
    <div className={styles.header}>
      <span className={styles.headerShimmer} aria-hidden>
        <span ref={shimmerInnerRef} className={styles.headerShimmerInner} />
      </span>
      <BreadCrumbsDisplay />
      <div className={styles.tools}>
        {tools}
        {/* <IconButton icon="help" symbolsOutlined /> */}
        <AccountPreview user={user} menuItems={profileMenuItems} />
      </div>
    </div>
  );
});

// Sidebar component (memoized) moved outside to reduce parent re-renders
interface SideBarProps {
  isCollapsed: boolean;
  sideBarClassName?: string;
  logoUrl: string;
  isSettingsVisible: boolean;
  onCloseSettings: () => void;
  onToggleCollapse: () => void;
  navItems: NavItem<undefined>[];
  settingsMenuNavItems: NavItem<undefined>[];
  settingsNavItems: NavItem<undefined>[];
  tooltipLabel: string;
}

const DashboardSideBar = React.memo(function DashboardSideBar(
  props: SideBarProps,
) {
  const {
    isCollapsed,
    sideBarClassName,
    logoUrl,
    isSettingsVisible,
    onCloseSettings,
    onToggleCollapse,
    navItems,
    settingsMenuNavItems,
    settingsNavItems,
    tooltipLabel,
  } = props;

  return (
    <div
      className={classNames(
        styles.sideBar,
        { [styles.sideBarCollapsed]: isCollapsed },
        sideBarClassName,
      )}
    >
      <div className={styles.toggleBtn}>
        <Tooltip body={tooltipLabel} key={tooltipLabel} placement="right">
          <IconButton
            icon={isCollapsed ? 'chevron_right' : 'chevron_left'}
            onClick={onToggleCollapse}
            rounded
            type={IconButtonTypes.SecondaryOutlined}
            size={IconButtonSizes.Small}
          />
        </Tooltip>
      </div>
      <div className={styles.sideBarTopBlock}>
        <NavLink
          to={logoUrl}
          className={isCollapsed ? styles.sideBarLogoSmall : styles.sideBarLogo}
          onClick={() => {
            if (isSettingsVisible) onCloseSettings();
          }}
        >
          {isCollapsed ? <LogoImage /> : <Logo />}
        </NavLink>
      </div>
      <div className={styles.sideBarScrollWrap}>
        <div className={styles.sideMenuWrap}>
          <Activity mode={isSettingsVisible ? 'hidden' : 'visible'}>
            <Menu
              navItems={navItems}
              isCollapsed={isCollapsed}
              itemWrapClassName={styles.menuItemWrapClassName}
            />
          </Activity>
          {settingsMenuNavItems && settingsMenuNavItems?.length > 0 && (
            <>
              <Activity mode={isSettingsVisible ? 'visible' : 'hidden'}>
                <Menu
                  navItems={settingsMenuNavItems}
                  isCollapsed={isCollapsed}
                  itemWrapClassName={styles.menuItemWrapClassName}
                />
              </Activity>
              <div className={styles.sideBarBottomBlock}>
                <Menu
                  navItems={settingsNavItems}
                  isCollapsed={isCollapsed}
                  itemWrapClassName={styles.menuItemWrapClassName}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

// Mount Outlet after the next animation frame to let header animation start smoothly
function DeferredOutlet({ context }: { context: object }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);
  if (!mounted) return null;
  return <Outlet context={context} />;
}

function DashboardLayout(props: Props): React.ReactElement {
  const {
    activeTechnicalPause,
    captureException,
    disableContentScroll,
    loading,
    logoUrl,
    navItems,
    outletContext,
    profileMenuItems,
    settingsMenuNavItems = [],
    settingsPaths = [],
    showPlatformSwitcher = false,
    showSidebarWhenTechnicalPause,
    sideBarClassName,
    technicalPauseWhiteUrlList = [],
    tools,
    topComponents,
    upcomingTechnicalPause,
    user,
  } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { phone, tablet } = isMobile();
  const isMobileDevice = phone || tablet;
  const [isBlockedUI, setBlockedUI] = useState(
    shouldBlockUiByDevice ? isMobileDevice : false,
  );
  const settingsMenuNavPaths = settingsMenuNavItems
    ?.map((item) => item.to)
    .filter(Boolean) as string[];
  const isSettingsPage = [...settingsMenuNavPaths, ...settingsPaths].some(
    (path) => {
      return path.startsWith(pathname);
    },
  );
  const [isSettingsVisible, setSettingsVisible] = useState(isSettingsPage);
  const technicalPauseEndDate = activeTechnicalPause?.endAt;
  const companyId = _get(user, 'company.id', '') as string;
  const { width } = useWindowDimensions();
  const [isCollapsed, setCollapsed] = useLocalStorage(
    DASHBOARD_LAYOUT_IS_COLLAPSED,
    false,
  );
  const [planModalType, setPlanModalType] = useState<PlanState | undefined>();

  const handleExpanded = useCallback(() => {
    startTransition(() => {
      setCollapsed((prevState: boolean) => !prevState);
    });
  }, [setCollapsed]);

  const handleClosePlanModal = useCallback(
    () => setPlanModalType(undefined),
    [],
  );
  const handleOpenPlanModal = useCallback(
    (state: PlanState) => setPlanModalType(state),
    [],
  );

  const handleOpenSettings = useCallback(() => {
    setSettingsVisible(true);
    const firstSettingItem = settingsMenuNavItems?.[0];
    if (firstSettingItem?.onClick) {
      firstSettingItem.onClick();
    } else if (firstSettingItem?.to) {
      startTransition(() => {
        navigate(firstSettingItem.to as string);
      });
    }
  }, [navigate, settingsMenuNavItems]);
  const handleCloseSettings = useCallback(() => setSettingsVisible(false), []);

  const contextProps = {
    ...outletContext,
    handleOpenPlanModal,
    user,
    technicalPauseEndDate,
  };

  const settingsNavItems = useMemo(
    () =>
      isSettingsVisible
        ? [
            {
              id: 'back',
              title: t('back'),
              onClick: handleCloseSettings,
              type: NavItemTypeEnum.Primary,
              end: true,
              leftIcon: Icons.arrowBack,
            },
          ]
        : [
            {
              id: 'settings',
              title: t('settings'),
              onClick: handleOpenSettings,
              type: NavItemTypeEnum.Primary,
              end: true,
              leftIcon: Icons.settings,
              predictorFnActive: () => isSettingsPage,
            },
          ],
    [
      isSettingsVisible,
      t,
      handleCloseSettings,
      handleOpenSettings,
      isSettingsPage,
    ],
  );

  // Precompute tooltip label; pass to memoized SideBar
  const tooltipLabel = t(isCollapsed ? 'expand' : 'collapse');

  useEffect(() => {
    if (shouldBlockUiByDevice) {
      setBlockedUI(width <= 1024 || isMobileDevice);
    }
  }, [isMobileDevice, width]);

  if (loading) {
    return <CircleLoader />;
  }

  if (isBlockedUI) {
    return (
      <ErrorPage
        status={ErrorPageStatusEnum.DesktopOnly}
        logoUrl={logoUrl}
        useSwitcher={showPlatformSwitcher}
        className={styles.errorPage}
      />
    );
  }

  const isTechnicalPauseWhiteUrl = technicalPauseWhiteUrlList.some(
    (url) => url === pathname,
  );

  const isTechnicalPauseActive = !!technicalPauseEndDate;
  const hideAll = isTechnicalPauseActive && !showSidebarWhenTechnicalPause;
  const hideContent = isTechnicalPauseActive && !isTechnicalPauseWhiteUrl;

  return (
    <>
      {topComponents}
      <ScrollToTop />
      <ReleaseTracker
        captureException={captureException}
        domain={domain}
        releaseVersion={releaseVersion}
        releaseTrackerSeconds={releaseTrackerSeconds}
        mode={mode}
      />
      <UpgradePlanModal
        companyId={companyId}
        isOpen={!!planModalType}
        planState={planModalType}
        onClose={handleClosePlanModal}
      />
      <Activity mode={hideAll ? 'hidden' : 'visible'}>
        <div
          className={classNames(styles.root, {
            [styles.rootCollapsed]: isCollapsed,
          })}
        >
          <DashboardSideBar
            isCollapsed={isCollapsed}
            sideBarClassName={sideBarClassName}
            logoUrl={logoUrl}
            isSettingsVisible={isSettingsVisible}
            onCloseSettings={handleCloseSettings}
            onToggleCollapse={handleExpanded}
            navItems={navItems}
            settingsMenuNavItems={settingsMenuNavItems}
            settingsNavItems={settingsNavItems}
            tooltipLabel={tooltipLabel}
          />

          <div
            className={classNames(styles.content, {
              [styles.contentNoScroll]: disableContentScroll,
            })}
          >
            <Activity mode={hideContent ? 'hidden' : 'visible'}>
              <DashboardHeader
                pathname={pathname}
                tools={tools}
                user={user}
                profileMenuItems={profileMenuItems}
              />
              <div
                className={classNames(styles.body, {
                  [styles.bodyNoScroll]: disableContentScroll,
                })}
              >
                <TechnicalPauseBanner
                  upcomingTechnicalPause={upcomingTechnicalPause}
                />
                <div className={styles.bodyContent}>
                  <ErrorBoundary
                    location={location}
                    captureException={captureException}
                  >
                    <React.Suspense fallback={null}>
                      <DeferredOutlet context={contextProps} />
                    </React.Suspense>
                  </ErrorBoundary>
                </div>
              </div>
            </Activity>

            {hideContent && (
              <TechnicalPauseTracker
                technicalPauseEndDate={technicalPauseEndDate}
              />
            )}
          </div>

          <div
            style={{
              position: 'fixed',
              left: '-100vw',
              top: '50%',
              zIndex: 9999,
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a id={downloadLinkId} download>
              Download link
            </a>
            <input type="text" id={emptyHiddenInputId} />
          </div>
        </div>
      </Activity>

      {hideAll && (
        <TechnicalPauseTracker technicalPauseEndDate={technicalPauseEndDate} />
      )}
    </>
  );
}

export default React.memo(DashboardLayout);
